import { forwardRef } from 'react';
import { Select, MenuItem, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql } from '../gql';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useSnackbar } from '../store/snackbar';

const journeyEditDrawer_getJourneyUniqueMetrics = graphql(`
	query journeyEditDrawer_getJourneyUniqueMetrics($journeyId: UUID!) {
		getJourney(by: { id: $journeyId }) {
			phases {
				contentMetrics {
					metric {
						id
						name
					}
				}
			}
			mainMetric {
				metric {
					id
				}
			}
		}
	}
`);

const journeyEditDrawer_setJourneyMainMetric = graphql(`
	mutation journeyEditDrawer_setJourneyMainMetric($journeyId: UUID!, $mainMetric: UUID!) {
		updateJourney(by: { id: $journeyId }, data: { mainMetric: { connect: { metric: { id: $mainMetric } } } }) {
			ok
			errorMessage
		}
	}
`);

const journeyEditDrawer_disconnectJourneyMainMetric = graphql(`
	mutation journeyEditDrawer_disconnectJourneyMainMetric($journeyId: UUID!) {
		updateJourney(by: { id: $journeyId }, data: { mainMetric: { disconnect: true } }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	journeyId: string;
}>;

export const SelectJourneyMainMetric = forwardRef<HTMLDivElement, Props>(({ journeyId }, ref) => {
	const intl = useIntl();
	const { data } = useApiQuery(journeyEditDrawer_getJourneyUniqueMetrics, {
		variables: { journeyId },
	});
	const { openSnackbar } = useSnackbar();

	const uniqueMetrics: { id: string; name: string }[] =
		data?.getJourney?.phases
			.flatMap((phase) => phase.contentMetrics.map((contentMetric) => contentMetric.metric))
			.filter(
				(metric): metric is { id: string; name: string } => !!metric && typeof metric === 'object' && 'id' in metric && 'name' in metric,
			) || [];

	const handleMainMetricChange = (mainMetricId: string | undefined) => {
		mainMetricId ? updateMainJourney({ journeyId: journeyId, mainMetric: mainMetricId }) : disconnectMainJourney({ journeyId });
	};

	const { mutate: updateMainJourney } = useApiMutation(journeyEditDrawer_setJourneyMainMetric, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.updateJourney.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateJourney.errorMessage,
				openSnackbar,
			});
		},
	});

	const { mutate: disconnectMainJourney } = useApiMutation(journeyEditDrawer_disconnectJourneyMainMetric, {
		onSuccess: async (result) => {
			mutationResponseHandler({
				result: result.updateJourney.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateJourney.errorMessage,
				openSnackbar,
			});
		},
	});

	return (
		<Select
			id={'journey-main-metrics'}
			displayEmpty
			ref={ref}
			onChange={(event) => handleMainMetricChange(event.target.value as string)}
			renderValue={() =>
				uniqueMetrics.find((item) => item.id === data?.getJourney?.mainMetric?.metric?.id)?.name || (
					<Typography fontSize={14} fontStyle={'italic'}>
						<FormattedMessage id={'form.select-main-metric-input.noMainMetric'} />
					</Typography>
				)
			}
		>
			{uniqueMetrics.length === 0 && (
				<MenuItem disabled value={''}>
					<em>
						<FormattedMessage id={'form.select-main-metric-input.nothingToSelect'} />
					</em>
				</MenuItem>
			)}
			{uniqueMetrics.length > 0 && (
				<MenuItem value={''}>
					<Typography fontSize={14} fontStyle={'italic'}>
						<FormattedMessage id={'form.select-main-metric-input.noMainMetric'} />
					</Typography>
				</MenuItem>
			)}

			{uniqueMetrics.map((option) => (
				<MenuItem key={option.id} value={option.id}>
					<Typography fontSize={14}>{option.name}</Typography>
				</MenuItem>
			))}
		</Select>
	);
});
